import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Globe from 'react-globe.gl';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { X, Globe2, Map as MapIcon } from 'lucide-react';
import './GlobeTimezoneModal.css';

interface GlobeTimezoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTimezone: (tz: string) => void;
}

const GLOBE_MARKERS = [
  { city: 'New York', lat: 40.7128, lng: -74.0060, tz: 'America/New_York', size: 1.5, color: '#10b981' },
  { city: 'London', lat: 51.5074, lng: -0.1278, tz: 'Europe/London', size: 1.5, color: '#10b981' },
  { city: 'Paris', lat: 48.8566, lng: 2.3522, tz: 'Europe/Paris', size: 1.2, color: '#10b981' },
  { city: 'Dubai', lat: 25.2048, lng: 55.2708, tz: 'Asia/Dubai', size: 1.5, color: '#10b981' },
  { city: 'Mumbai', lat: 19.0760, lng: 72.8777, tz: 'Asia/Kolkata', size: 1.5, color: '#10b981' },
  { city: 'Singapore', lat: 1.3521, lng: 103.8198, tz: 'Asia/Singapore', size: 1.5, color: '#10b981' },
  { city: 'Tokyo', lat: 35.6762, lng: 139.6503, tz: 'Asia/Tokyo', size: 1.5, color: '#10b981' },
  { city: 'Sydney', lat: -33.8688, lng: 151.2093, tz: 'Australia/Sydney', size: 1.5, color: '#10b981' },
  { city: 'Los Angeles', lat: 34.0522, lng: -118.2437, tz: 'America/Los_Angeles', size: 1.5, color: '#10b981' },
  { city: 'Chicago', lat: 41.8781, lng: -87.6298, tz: 'America/Chicago', size: 1.2, color: '#10b981' },
  { city: 'Sao Paulo', lat: -23.5505, lng: -46.6333, tz: 'America/Sao_Paulo', size: 1.5, color: '#10b981' },
  { city: 'Johannesburg', lat: -26.2041, lng: 28.0473, tz: 'Africa/Johannesburg', size: 1.2, color: '#10b981' },
];

export const GlobeTimezoneModal: React.FC<GlobeTimezoneModalProps> = ({ isOpen, onClose, onSelectTimezone }) => {
  const globeRef = useRef<any>(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 500 });
  const [hoveredCity, setHoveredCity] = useState<{ name: string, x: number, y: number } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (globeRef.current) {
          globeRef.current.controls().autoRotate = true;
          globeRef.current.controls().autoRotateSpeed = 1;
        }
      }, 500);

      const updateDimensions = () => {
        const isMobile = window.innerWidth < 768;
        setDimensions({
          width: isMobile ? window.innerWidth - 40 : Math.min(800, window.innerWidth - 80),
          height: isMobile ? 350 : 300
        });
      };

      updateDimensions();
      window.addEventListener('resize', updateDimensions);
      return () => window.removeEventListener('resize', updateDimensions);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="globe-modal-overlay" onClick={onClose}>
      <div className="globe-modal-content" onClick={e => e.stopPropagation()}>
        <div className="globe-modal-header">
          <div className="globe-modal-header-top">
            <div>
              <h3>Select Time Zone</h3>
              <p>Click a major city to select its time zone automatically.</p>
            </div>
          </div>
        </div>

        <button className="globe-modal-close" onClick={onClose} style={{ zIndex: 1000 }}>
          <X size={20} />
        </button>

        <div className="globe-container">
          <div className="map-2d-wrapper" style={{ width: dimensions.width, position: 'relative' }}>
            <ComposableMap width={800} height={400} projectionConfig={{ scale: 140 }}>
              <Geographies geography="/features.json">
                {({ geographies }: { geographies: any[] }) =>
                  geographies.map((geo: any) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill="#e2e8f0"
                      stroke="#cbd5e1"
                      style={{
                        default: { outline: 'none' },
                        hover: { fill: '#cbd5e1', outline: 'none' },
                        pressed: { fill: '#94a3b8', outline: 'none' },
                      }}
                    />
                  ))
                }
              </Geographies>

              {GLOBE_MARKERS.map(({ city, lng, lat, tz }) => (
                <Marker key={city} coordinates={[lng, lat]} onClick={() => { onSelectTimezone(tz); onClose(); }}>
                  <circle
                    r={6}
                    fill="var(--primary)"
                    stroke="#fff"
                    strokeWidth={2}
                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={(e) => setHoveredCity({ name: city, x: e.clientX, y: e.clientY })}
                    onMouseMove={(e) => setHoveredCity({ name: city, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setHoveredCity(null)}
                  />
                </Marker>
              ))}
            </ComposableMap>

            {hoveredCity && (
              <div
                className="map-tooltip"
                style={{
                  position: 'fixed',
                  left: hoveredCity.x + 15,
                  top: hoveredCity.y + 15,
                  right: 'auto'
                }}
              >
                {hoveredCity.name}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
